/**
 * Entitlement catalog + plan matrix — MIRROR COPY (frontend).
 *
 * CANONICAL copy: functions/payments/shared/entitlements.js (billing is
 * authoritative). Vite and Cloud Functions cannot import across packages, so
 * the data is duplicated; src/config/entitlements.sync.test.js fails if the
 * two copies drift. Edit BOTH files together, always.
 *
 * See Documentation/Ideation/SUBSCRIPTION_ENTITLEMENTS_PLAN.md §3.
 */

// Internal subscription levels. "old_product" normalizes to basic.
export const LEVELS = ["basic", "plus", "advanced"];

export const TIER_DISPLAY_NAMES = {
  basic: "Basic",
  plus: "Essentials",
  advanced: "Advanced",
};

// null = unlimited
export const UNLIMITED = null;

// Every gateable boolean entitlement key.
export const ENTITLEMENT_KEYS = [
  "donorManagement",
  "bankIntegration",
  "labels",
  "mlAutoCategorization",
  "payroll",
  "savedReportPackages",
  "autoReconciliation",
  "consolidatedReporting",
  "linkedAccounts",
  "fundBudgeting",
  "t3Donations",
  "supportRaising",
  "accrualAccounting",
];

// Plan → entitlements matrix (§3.1). "none" = no subscription at all.
export const PLAN_MATRIX = {
  none: {
    donorManagement: false,
    bankIntegration: false,
    labels: false,
    mlAutoCategorization: false,
    payroll: false,
    savedReportPackages: false,
    autoReconciliation: false,
    consolidatedReporting: false,
    linkedAccounts: false,
    fundBudgeting: false,
    t3Donations: false,
    supportRaising: false,
    accrualAccounting: false,
  },
  basic: {
    donorManagement: true,
    bankIntegration: false,
    labels: false,
    mlAutoCategorization: false,
    payroll: false,
    savedReportPackages: false,
    autoReconciliation: false,
    consolidatedReporting: false,
    linkedAccounts: false,
    fundBudgeting: false,
    t3Donations: false,
    supportRaising: false,
    accrualAccounting: false,
  },
  plus: {
    donorManagement: true,
    bankIntegration: true,
    labels: true,
    mlAutoCategorization: true,
    payroll: true,
    savedReportPackages: true,
    autoReconciliation: false,
    consolidatedReporting: false,
    linkedAccounts: false,
    fundBudgeting: false,
    t3Donations: true,
    supportRaising: false,
    accrualAccounting: false,
  },
  advanced: {
    donorManagement: true,
    bankIntegration: true,
    labels: true,
    mlAutoCategorization: true,
    payroll: true,
    savedReportPackages: true,
    autoReconciliation: true,
    consolidatedReporting: true,
    linkedAccounts: true,
    fundBudgeting: true,
    t3Donations: true,
    supportRaising: true,
    accrualAccounting: true,
  },
};

// Numeric limits per plan (§3.3). Enforced server-side at connect/invite
// choke points; grandfathered orgs skip enforcement (§6.4).
export const PLAN_LIMITS = {
  none: { activeIntegrations: 0, nonAuditorUsers: 0 },
  basic: { activeIntegrations: 0, nonAuditorUsers: 1 },
  plus: { activeIntegrations: 2, nonAuditorUsers: 2 },
  advanced: { activeIntegrations: UNLIMITED, nonAuditorUsers: UNLIMITED },
};

// Map any raw subscriptionLevel value to a plan key.
export const normalizeLevel = (level) => {
  if (level === "old_product") return "basic"; // legacy products = basic
  return LEVELS.includes(level) ? level : "none";
};


// Lowest plan level that includes an entitlement — drives upsell copy like
// "Requires Essentials". Returns null when no plan grants it.
export const minLevelFor = (entitlementKey) => {
  for (const level of LEVELS) {
    if (PLAN_MATRIX[level][entitlementKey]) return level;
  }
  return null;
};

// User-facing tier name for any raw level value. Unknown/none levels fall
// back to a capitalized passthrough rather than leaking internal keys.
export const tierDisplayName = (level) => {
  const name = TIER_DISPLAY_NAMES[normalizeLevel(level)];
  if (name) return name;
  return level ? level.charAt(0).toUpperCase() + level.slice(1) : "";
};


// Composition used by every tier-aware lock surface: "which tier name do I
// show for this entitlement's lock copy?"
export const tierNameFor = (entitlementKey) => tierDisplayName(minLevelFor(entitlementKey));

/**
 * Derive the plan level from a subscription doc
 * (orgs/{id}/organizationAndSettings/subscription).
 * Demo, free-trial, and manual subscriptions carry no Stripe level; they get
 * full mid-tier ("plus") access today — matches setupInitialSubscription's
 * trialFeatures and the legacy Model B isDemo/freeTrial fallbacks.
 */
export const levelForSubscription = (subscriptionData, atMillis) => {
  if (!subscriptionData) return "none";
  if (subscriptionData.isDemo) return "plus";
  // Grace window (§6.6): a staged downgrade keeps the FROM level's
  // entitlements until effectiveAt — nothing is revoked during the countdown.
  const pd = subscriptionData.pendingDowngrade;
  if (pd && pd.fromLevel && pd.effectiveAt) {
    const eff = pd.effectiveAt.toMillis
      ? pd.effectiveAt.toMillis()
      : new Date(pd.effectiveAt).getTime();
    const now = atMillis != null ? atMillis : Date.now();
    if (Number.isFinite(eff) && now < eff) {
      return normalizeLevel(pd.fromLevel);
    }
  }
  const active =
    subscriptionData.subscriptionCurrent || subscriptionData.freeTrial;
  if (!active) return "none";
  if (subscriptionData.subscriptionLevel) {
    return normalizeLevel(subscriptionData.subscriptionLevel);
  }
  return "plus"; // active trial / manual subscription
};

/**
 * Effective entitlements for an org.
 * @param {string|null} level   subscriptionLevel ("basic"|"plus"|"advanced"|"old_product"|null)
 * @param {Object} [featureOverrides] orgs/{id}.featureOverrides — admin per-org
 *   overrides: true force-enables, false force-disables, absent = plan default.
 * @returns {Object} { [entitlementKey]: boolean }
 */
export const resolveEntitlements = (level, featureOverrides) => {
  const effective = { ...PLAN_MATRIX[normalizeLevel(level)] };
  if (featureOverrides && typeof featureOverrides === "object") {
    for (const key of ENTITLEMENT_KEYS) {
      if (typeof featureOverrides[key] === "boolean") {
        effective[key] = featureOverrides[key];
      }
    }
  }
  return effective;
};

/**
 * Numeric limits for an org's plan. Overrides do not apply to limits.
 * @returns {Object} { activeIntegrations, nonAuditorUsers } — null = unlimited
 */
export const resolveLimits = (level) => ({
  ...PLAN_LIMITS[normalizeLevel(level)],
});

/**
 * Migration helper: diff a legacy `features` map against plan defaults and
 * return the divergent boolean keys as explicit overrides, so pre-redesign
 * admin toggles are preserved instead of silently flattened. Absent keys are
 * "never set", not an admin decision — they mint nothing.
 */
export const mintLegacyOverrides = (currentFeatures, planDefaults) => {
  const overrides = {};
  if (currentFeatures && typeof currentFeatures === "object") {
    for (const key of ENTITLEMENT_KEYS) {
      if (
        typeof currentFeatures[key] === "boolean" &&
        currentFeatures[key] !== planDefaults[key]
      ) {
        overrides[key] = currentFeatures[key];
      }
    }
  }
  return overrides;
};
